import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  selectCurrentUser,
  selectIsAuthenticated,
} from "@/redux/authentication/selectors";
import { setAuthenticationState } from "@/redux/authentication/reducer";
import { LocalStorageName } from "@/redux/api";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseButton from "@/components_v2/Button/BaseButton";
import { Roles } from "@/shared/utils/constants";
import { hasCapability } from "@/shared/utils/roles";
import type { ProtectedRouteProps } from "@/router/types";
import { BUTTON_TYPES } from "@/components_v2/Button/constants";

import "./styles.css";

const AccessDenied = ({ message }: { message?: string }) => {
  const { t } = useTranslation();

  return (
    <div className="access-denied-container flex flex-col gap-300 items-center full-width">
      <h1 className="header-l-medium">{t("labels.accessDenied")}</h1>
      <p className="body-m-regular">
        {message || t("labels.noPermissionForPage")}
      </p>
      <BaseButton size="small" to={"/"} type={BUTTON_TYPES.LINK}>
        {t("labels.returnToHome")}
      </BaseButton>
    </div>
  );
};

const ProtectedRoute = ({
  element,
  roles = [],
  organizationRoles = [],
  requiredCapability,
}: ProtectedRouteProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      checkAccess();
      setIsLoading(false);
    } else if (isAuthenticated) {
      globalThis.localStorage.removeItem(LocalStorageName);
      dispatch(setAuthenticationState({ isAuthenticated: false }));
    }
  }, [
    currentUser,
    isAuthenticated,
    roles,
    organizationRoles,
    requiredCapability,
  ]);

  const checkAccess = () => {
    if (!currentUser) return;

    if (requiredCapability && !hasCapability(currentUser, requiredCapability)) {
      setHasAccess(false);
      return;
    }

    const { role, organizationRole } = currentUser;
    const hasAllowedRole = roles?.includes(role) ?? false;

    if (role === Roles.ADMIN.value) {
      setHasAccess(hasAllowedRole);
      return;
    }

    if (role === Roles.USER.value) {
      setHasAccess(
        hasAllowedRole &&
          organizationRole != null &&
          organizationRoles.includes(organizationRole),
      );
      return;
    }

    setHasAccess(false);
  };

  if (!isAuthenticated) {
    return <Navigate to={`/login?redirect_to=${location.pathname}`} />;
  }

  if (isLoading) {
    return <BaseSpinner />;
  }

  if (!currentUser) {
    return <AccessDenied message="Error loading user information" />;
  }

  return hasAccess ? element : <AccessDenied />;
};

export default ProtectedRoute;
